import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CheckCircleOutline, ErrorOutlineOutlined } from '@mui/icons-material';
import { Grid, Stack } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { PRIMARY_GREEN, PRIMARY_YELLOW } from 'commons/styles/colors';
import { IInboundTaskItemWithSummary } from 'commons/types';
import { formatDate } from 'commons/utils/formatDate';
import {
  confirmInboundInvoiceAction,
  getInProgressInboundInvoiceAction,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { INBOUND_ROUTES } from 'routes/constants';

import { InboundSkuReceiveList } from './components';
import S from './InboundSummary.styles';

export const InboundSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { inboundInvoice } = useSelector(
    (state: RootReducerInterface) => state.inboundInvoice,
  );

  const [showPartialConfirmation, setShowPartialConfirmation] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    dispatch(getInProgressInboundInvoiceAction()).then(() => {
      setDataFetched(true);
    });
  }, [dispatch]);

  const { completeSku, incompleteSku, invalidSku } = useMemo(() => {
    function _skuSort(
      sku1: IInboundTaskItemWithSummary,
      sku2: IInboundTaskItemWithSummary,
    ): number {
      // sort by quantity
      if (sku1.receivedQty < sku2.receivedQty) return -1;
      if (sku1.receivedQty > sku2.receivedQty) return 1;

      // sort by sku code
      return sku1.sku_code < sku2.sku_code ? -1 : 1;
    }
    const _completeSku: IInboundTaskItemWithSummary[] = [];
    const _incompleteSku: IInboundTaskItemWithSummary[] = [];
    const _invalidSku: IInboundTaskItemWithSummary[] = [];

    inboundInvoice?.items?.forEach((item) => {
      const receivedQty = item.receive_actual.reduce((total, { quantity }) => {
        return total + quantity;
      }, 0);

      if (receivedQty === item.quantity) {
        _completeSku.push({ ...item, receivedQty });
      } else {
        _incompleteSku.push({ ...item, receivedQty });
      }

      // for now, invalid skus defined as item with receivedQty > quantity
      if (receivedQty > item.quantity || receivedQty === 0) {
        _invalidSku.push({ ...item, receivedQty });
      }
    });

    return {
      completeSku: _completeSku.sort(_skuSort),
      incompleteSku: _incompleteSku.sort(_skuSort),
      invalidSku: _invalidSku.sort(_skuSort),
    };
  }, [inboundInvoice]);

  if (!inboundInvoice) {
    if (dataFetched) navigate(INBOUND_ROUTES.ASSIGN);
    return <div />;
  }

  const handleBackToDestination = (): void => {
    navigate(INBOUND_ROUTES.LIST);
  };

  const handleCompleteInboundTask = (): void => {
    if (inboundInvoice.items) {
      dispatch(
        confirmInboundInvoiceAction(() => navigate(INBOUND_ROUTES.ASSIGN)),
      );
    }
  };

  const handleConfirmationCheckOnPartialReceive = (): void => {
    if (incompleteSku.length > 0) {
      setShowPartialConfirmation(true);
    } else {
      handleCompleteInboundTask();
    }
  };

  return (
    <>
      <S.ContentWrapper>
        <S.HeaderWrapper>
          <S.Header
            color={incompleteSku.length > 0 ? PRIMARY_YELLOW : PRIMARY_GREEN}
          >
            {incompleteSku.length > 0 ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <ErrorOutlineOutlined /> Receive Incomplete
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={1}>
                <CheckCircleOutline /> Receive Completed
              </Stack>
            )}
          </S.Header>
        </S.HeaderWrapper>
        <S.TaskInfoWrapper container rowGap={1}>
          <S.TaskNumberText item xs={12} data-testid="InboundSummaryTaskNumber">
            {inboundInvoice.inbound_task_number}
          </S.TaskNumberText>

          <S.InvoiceInfoText
            item
            xs={12}
            data-testid="InboundSummaryInvoiceNumber"
          >
            {`Invoice Number: ${inboundInvoice.invoice_number}`}
          </S.InvoiceInfoText>
          <S.InvoiceInfoText
            item
            xs={12}
            data-testid="InboundSummaryInvoiceDate"
          >
            {`Invoice Date: ${formatDate(
              inboundInvoice.invoice_date,
              'dd/LL/yyyy',
            )}`}
          </S.InvoiceInfoText>
        </S.TaskInfoWrapper>

        {/* incomplete */}
        {incompleteSku.length > 0 && (
          <InboundSkuReceiveList
            inboundTaskItems={incompleteSku}
            defaultExpanded
          />
        )}

        {/* complete */}
        {completeSku.length > 0 && (
          <InboundSkuReceiveList inboundTaskItems={completeSku} />
        )}
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      <ConfirmationDialog
        openModal={showPartialConfirmation}
        title="Incomplete Receive"
        content="Some SKUs have not been fully received. Are you sure you want to complete this task?"
        onClose={() => setShowPartialConfirmation(false)}
        onConfirm={handleCompleteInboundTask}
        dataTestIdOnClose="BtnInboundSummaryPartialConfirmNo"
        dataTestIdOnConfirm="BtnInboundSummaryPartialConfirmYes"
      />

      {/* Footer */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={handleBackToDestination}
              data-testid="BtnInboundSummaryGoBack"
            >
              GO BACK
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              disabled={invalidSku.length > 0}
              onClick={handleConfirmationCheckOnPartialReceive}
              data-testid="BtnInboundSummaryComplete"
            >
              COMPLETE TASK
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </>
  );
};
